import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BookOpen, Download, ArrowLeft, Loader2, Eye } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { renderMarkdown, fetchManualText, downloadManual } from "../lib/markdown";
import EmptyState from "../components/EmptyState";
import dict from "../i18n/es-MX";

export default function Manuales() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [selected, setSelected] = useState(null); // { filename }
    const [content, setContent] = useState("");
    const [contentLoading, setContentLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true); setErr("");
        try {
            const r = await api.get("/manuals");
            setItems(r.data?.items || []);
        } catch (e) { setErr(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const openManual = async (filename) => {
        setSelected({ filename });
        setContent(""); setContentLoading(true);
        try {
            const text = await fetchManualText(filename);
            setContent(text);
        } catch (e) { setContent(""); setErr(dict.manuals.loadError); }
        finally { setContentLoading(false); }
    };

    const html = useMemo(() => renderMarkdown(content), [content]);

    if (selected) {
        const meta = dict.manuals.items[selected.filename] || { title: selected.filename };
        return (
            <div data-testid="manuales-view" className="space-y-5 max-w-3xl">
                <button type="button" onClick={() => setSelected(null)} data-testid="manuales-back"
                    className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-100">
                    <ArrowLeft size={14}/>{dict.manuals.backToList}
                </button>
                <header className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{meta.title}</h1>
                        <p className="mt-2 text-slate-400 text-sm">{meta.description}</p>
                    </div>
                    <button type="button" onClick={() => downloadManual(selected.filename)}
                        data-testid="manuales-download-btn"
                        className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-700 hover:border-brand-orange text-sm">
                        <Download size={14}/>{dict.manuals.download}
                    </button>
                </header>
                <article data-testid="manuales-article"
                    className="rounded-xl border border-slate-800 bg-slate-900/40 p-6 prose prose-invert max-w-none">
                    {contentLoading ? (
                        <div className="py-10 text-center text-slate-500"><Loader2 className="animate-spin inline mr-2"/>{dict.common.loading || "Cargando…"}</div>
                    ) : (
                        <div dangerouslySetInnerHTML={{ __html: html }}/>
                    )}
                </article>
            </div>
        );
    }

    return (
        <div data-testid="manuales-page" className="space-y-5 max-w-3xl">
            <header>
                <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">{dict.manuals.title}</h1>
                <p className="mt-2 text-slate-400 text-sm">{dict.manuals.subtitle}</p>
            </header>

            {loading && <div className="py-10 text-center text-slate-500"><Loader2 className="animate-spin inline"/></div>}

            {!loading && err && <div className="rounded-lg border border-red-500/40 bg-red-900/20 p-4 text-sm text-red-300" data-testid="manuales-error">{err}</div>}

            {!loading && !err && items.length === 0 && (
                <EmptyState icon={BookOpen} title={dict.manuals.empty} testid="manuales-empty"/>
            )}

            {!loading && !err && items.length > 0 && (
                <div className="grid gap-3" data-testid="manuales-list">
                    {items.map((it) => {
                        const meta = dict.manuals.items[it.filename] || { title: it.filename, description: "" };
                        return (
                            <div key={it.filename} data-testid={`manuales-item-${it.filename}`}
                                className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 flex items-start gap-4 hover:border-brand-orange/60 transition">
                                <div className="w-12 h-12 rounded-xl bg-brand-orange/15 flex items-center justify-center flex-shrink-0">
                                    <BookOpen size={24} className="text-brand-orange"/>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-display text-lg font-bold text-slate-50">{meta.title}</h3>
                                    <p className="text-sm text-slate-400 mt-1">{meta.description}</p>
                                </div>
                                <div className="flex flex-col gap-2 flex-shrink-0">
                                    <button type="button" onClick={() => openManual(it.filename)}
                                        data-testid={`manuales-view-${it.filename}`}
                                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-brand-orange text-slate-950 font-semibold text-xs uppercase tracking-wider hover:bg-orange-400">
                                        <Eye size={14}/>{dict.manuals.view}
                                    </button>
                                    <button type="button" onClick={() => downloadManual(it.filename)}
                                        data-testid={`manuales-download-${it.filename}`}
                                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-700 hover:border-brand-orange text-xs uppercase tracking-wider text-slate-300">
                                        <Download size={14}/>{dict.manuals.download}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
